import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../../core/common/dataTable/index";
import { TableData } from "../../../../core/data/interface";
import {
  updateInterviewStatus,
  scheduleInterview,
  getInterviewList,
} from "../../../../service/registration";
import { all_routes } from "../../../router/all_routes";
import { formatDate, formatDateTime } from "../../../../utils/dateutils";
import { useSelector } from "react-redux";
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice";
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice";
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice";
import CommonSelect, { Option } from "../../../../core/common/commonSelect";
import {
  gender,
  SORT_OPTIONS,
  status,
} from "../../../../core/common/selectoption/selectoption";
import { Class, getAllClasses } from "../../../../service/classes";
import TooltipOption from "../../../../core/common/tooltipOption";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { campusBrandName, campusBranchLabel } from "../../../../utils/campusLogos";

interface RegistrationData extends TableData {
  registrationNumber: string;
  firstName: string;
  gender: string;
  status: string;
  createdAt: string;
  dateOfBirth: string;
  fatherEmail: string;
  fatherPhoneNumber: string;
  _id: string;
  lastName: string;
  studentPicture: string;
  academicYear: string;
  testStatus?: string;
  interviewDate?: string;
  interviewTime?: string;
  interviewStatus?: string;
}

const InterviewStatus = () => {
  const componentRef = useRef<HTMLDivElement>(null);
  const routes = all_routes;
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentSession = useSelector(selectCurrentSession);
  const currentBranch = useSelector(selectCurrentBranch);
  const [data, setData] = useState<RegistrationData[]>([]);
  const [loading, setLoading] = useState(true);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [pagination, setPagination] = useState({ limit: 10, page: 1 });
  const [paginationData, setPaginationData] = useState<any>();
  const [classesLoading, setClassesLoading] = useState(false);
  const [classes, setAllClasses] = useState<Option[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedRegistration, setSelectedRegistration] =
    useState<RegistrationData | null>(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    interviewDate: "",
    interviewTime: "",
  });
  const [filters, setFilters] = useState<{
    class?: Option;
    gender?: Option;
    status?: Option;
  } | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string;
    gender?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    system?: string;
  } | null>({ system: systems[0]._id });

  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);

  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await getInterviewList({
        ...pagination,
        ...appliedFilters,
        ...sortBy.value,
        session: currentSession?._id,
        searchText,
      });

      setData(response.registrations);
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Error fetching registrations:", error);
      toast.error("Failed to fetch registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [pagination, appliedFilters, sortBy, currentSession?._id, searchText]);

  const fetchClasses = async () => {
    setClassesLoading(true);
    try {
      const response = await getAllClasses({ system: appliedFilters?.system });
      const formattedClasses = response.classes.map((classItem: Class) => ({
        label: classItem.name,
        value: classItem._id,
      }));
      setAllClasses(formattedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, [appliedFilters?.system]);

  const handleInterviewResult = async (
    registrationId: string,
    result: "passed" | "failed"
  ) => {
    setLoading(true);
    try {
      const response = await updateInterviewStatus(registrationId, result);
      toast.success(response.message);
      fetchRegistrations();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const handleInterviewFormChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setInterviewForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const to12Hour = (time: string) => {
    if (!time || !time.includes(":")) return "";

    const [hours, minutes] = time.split(":").slice(0, 2).map(Number);

    if (isNaN(hours)) return "";

    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;

    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const handleEditInterview = (record: RegistrationData) => {
    setSelectedRegistration(record);
    setInterviewForm({
      interviewDate: record.interviewDate
        ? formatDateForInput(record.interviewDate)
        : "",
      interviewTime: record.interviewTime || "",
    });
    setShowInterviewModal(true);
  };

  const handleInterviewSubmit = async () => {
    if (!interviewForm.interviewDate || !interviewForm.interviewTime) {
      toast.error("Please select both date and time");
      return;
    }

    if (!selectedRegistration) {
      toast.error("No candidate selected");
      return;
    }

    try {
      setLoading(true);

      const [hours, minutes] = interviewForm.interviewTime.split(":");
      const formattedTime = `${hours.padStart(2, "0")}:${minutes.padStart(
        2,
        "0"
      )}`;

      const response = await scheduleInterview(selectedRegistration._id, {
        interviewDate: interviewForm.interviewDate,
        interviewTime: formattedTime,
      });

      toast.success(response.message);
      await fetchRegistrations();
      setShowInterviewModal(false);
      setInterviewForm({
        interviewDate: "",
        interviewTime: "",
      });
    } catch (error: any) {
      console.error("Error scheduling interview:", error);
      toast.error(
        error.response?.data?.message || "Failed to schedule interview"
      );
    } finally {
      setLoading(false);
    }
  };

  const openDatePicker = () =>
    dateRef.current?.showPicker?.() || dateRef.current?.focus();
  const openTimePicker = () =>
    timeRef.current?.showPicker?.() || timeRef.current?.focus();

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }

    setAppliedFilters((prev) => ({
      ...prev,
      class: filters?.class?.value as string,
      gender: filters?.gender?.value as string,
      status: filters?.status?.value as string,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      class: {
        label: "Select",
        value: "",
      },
      gender: {
        label: "Select",
        value: "",
      },
      status: {
        label: "Select",
        value: "",
      },
    });
    setAppliedFilters((prev) => ({
      ...prev,
      class: "",
      gender: "",
      status: "",
    }));
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        "Registration Number": item.registrationNumber,
        "Student Name": `${item.firstName} ${item.lastName}`,
        "Applied Class": item.appliedClass.name,
        Gender: item.gender,
        Status: item.status,
        "process Starts At": formatDateTime(item.createdAt),
        Email: item.fatherEmail,
        Phone: item.fatherPhoneNumber,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "interviews");
    XLSX.writeFile(wb, "interviews.xlsx");
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "registrationNumber",
      render: (text: string) => (
        <Link to="#" className="link-primary">
          {text}
        </Link>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "firstName",
      render: (_text: string, record: RegistrationData) => (
        <span>{`${record.firstName} ${record.lastName}`}</span>
      ),
    },
    {
      title: "Class",
      dataIndex: "name",
      render: (_text: string, record: TableData) => (
        <span>{record.appliedClass.name}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "fatherEmail",
    },
    {
      title: "Phone",
      dataIndex: "fatherPhoneNumber",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <span
          className={`badge badge-soft-${
            status === "accepted"
              ? "success"
              : status === "rejected"
              ? "danger"
              : "warning"
          }`}
        >
          {status || "Pending"}
        </span>
      ),
    },
    {
      title: "Interview Date/Time",
      dataIndex: "interviewDate",
      render: (date: string, record: RegistrationData) => {
        if (!date && !record.interviewTime) return <span>Not Scheduled</span>;
        return (
          <span>
            {formatDate(date)}{" "}
            {record.interviewTime && to12Hour(record.interviewTime)}
          </span>
        );
      },
    },
    {
      title: "Interview Status",
      dataIndex: "interviewStatus",
      render: (status: string) => (
        <span
          className={`badge badge-soft-${
            status === "passed"
              ? "success"
              : status === "failed"
              ? "danger"
              : "warning"
          }`}
        >
          {status || "Pending"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      className: "no-print",
      render: (_text: string, record: RegistrationData) => (
        <div className="d-flex gap-2">
          {record.interviewStatus === "passed" ? (
            <Link
              className="btn btn-primary btn-sm"
              to={all_routes.addAdmissionForm}
              state={{ ...record, fromRegistration: true }}
            >
              Proceed to Admission
            </Link>
          ) : record.interviewStatus === "failed" ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleInterviewResult(record._id, "passed")}
            >
              Promote
            </button>
          ) : record.interviewDate ? (
            <>
              <button
                className={`btn btn-sm ${
                  record.interviewStatus === "passed"
                    ? "btn-success"
                    : "btn-outline-success"
                }`}
                onClick={() => handleInterviewResult(record._id, "passed")}
              >
                Pass
              </button>
              <button
                className={`btn btn-sm ${
                  record.interviewStatus === "failed"
                    ? "btn-danger"
                    : "btn-outline-danger"
                }`}
                onClick={() => handleInterviewResult(record._id, "failed")}
              >
                Fail
              </button>

              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleEditInterview(record)}
              >
                Edit Interview Schedule
              </button>
            </>
          ) : (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleEditInterview(record)}
            >
              Schedule Interview
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Interview List</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.admissionFormList}>Registrations</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  All Interview List
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption
              onRefresh={fetchRegistrations}
              onExportPdf={handlePrint}
              onExportExcel={handleExportExcel}
            />
            <div className="mb-2">
              <Link
                to={routes.addRegistrationForm}
                className="btn btn-primary d-flex align-items-center"
              >
                <i className="ti ti-square-rounded-plus me-2" />
                New Registration
              </Link>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Interview List</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <CommonSelect
                  className="select"
                  options={systems}
                  value={systems.find(
                    (item) => item._id === appliedFilters?.system
                  )}
                  getOptionValue={(item) => item._id}
                  getOptionLabel={(item) => item.name}
                  onChange={(systemItem) =>
                    setAppliedFilters((prev) => ({
                      ...prev,
                      system: systemItem?._id,
                    }))
                  }
                />
              </div>
              <div className="dropdown mb-3 me-2">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </Link>
                <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                  <form>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <h4>Filter</h4>
                    </div>
                    <div className="p-3 pb-0 border-bottom">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label className="form-label">Class</label>
                            {classesLoading ? (
                              <div> Loading classes... </div>
                            ) : (
                              <CommonSelect
                                className="select"
                                options={classes}
                                value={filters?.class}
                                onChange={(classItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    class: classItem as Option,
                                  }))
                                }
                              />
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Gender</label>
                            <CommonSelect
                              className="select"
                              options={gender}
                              value={filters?.gender}
                              onChange={(genderItem) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  gender: genderItem as Option,
                                }))
                              }
                            />
                          </div>
                        </div>
                        {/* <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Fee Status</label>
                            <CommonSelect
                              className="select"
                              options={status}
                              value={filters?.status}
                              onChange={statusItem =>
                                setFilters(prev => ({
                                  ...prev,
                                  status: statusItem as Option,
                                }))
                              }
                            />
                          </div>
                        </div> */}
                      </div>
                    </div>
                    <div className="p-3 d-flex align-items-center justify-content-end">
                      <div
                        className="btn btn-light me-3"
                        onClick={handleResetFilters}
                      >
                        Reset
                      </div>
                      <div
                        className="btn btn-primary"
                        onClick={handleApplyClick}
                      >
                        Apply
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="dropdown mb-3">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by {sortBy.label}
                </Link>
                <ul className="dropdown-menu p-3">
                  {SORT_OPTIONS.map((item, index) => (
                    <li key={index}>
                      <div
                        className={`dropdown-item rounded-1 ${
                          item.label === sortBy.label ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSortSelect(item)}
                      >
                        {item.label}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div
            className="card-body p-0 py-3 printable-content"
            ref={componentRef}
          >
            <div className="print-only-header">
              <div className="school-brand">
                <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
              </div>
              <div className="report-title">Interview List</div>
              <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
            </div>
            <Table
              columns={columns}
              dataSource={data}
              pagination={{ ...paginationData, onChange: handleChange }}
              loading={loading}
              setSearchText={setSearchText}
            />
          </div>
        </div>
      </div>

      {showInterviewModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          id="interview_modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  {selectedRegistration?.interviewDate
                    ? "Edit Interview Schedule"
                    : "Schedule Interview"}
                </h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => {
                    setShowInterviewModal(false);
                    setInterviewForm({
                      interviewDate: "",
                      interviewTime: "",
                    });
                  }}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Interview Date</label>
                      <div className="input-icon position-relative">
                        <input
                          type="text"
                          className="form-control"
                          readOnly
                          value={interviewForm.interviewDate}
                          onClick={openDatePicker}
                          placeholder="Select Date"
                        />
                        <span
                          className="input-icon-addon"
                          onClick={openDatePicker}
                        >
                          <i className="ti ti-calendar" />
                        </span>
                        <input
                          ref={dateRef}
                          type="date"
                          name="interviewDate"
                          value={interviewForm.interviewDate}
                          onChange={handleInterviewFormChange}
                          required
                          min={new Date().toISOString().split("T")[0]}
                          className="position-absolute opacity-0"
                          style={{ width: 0, height: 0 }}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Interview Time</label>
                      <div className="input-icon position-relative">
                        <input
                          type="text"
                          className="form-control"
                          readOnly
                          value={to12Hour(interviewForm.interviewTime)}
                          onClick={openTimePicker}
                          placeholder="Select Time"
                        />
                        <span
                          className="input-icon-addon"
                          onClick={openTimePicker}
                        >
                          <i className="ti ti-clock" />
                        </span>
                        <input
                          ref={timeRef}
                          type="time"
                          name="interviewTime"
                          value={interviewForm.interviewTime}
                          onChange={handleInterviewFormChange}
                          required
                          className="position-absolute opacity-0"
                          style={{ width: 0, height: 0 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setShowInterviewModal(false);
                    setInterviewForm({
                      interviewDate: "",
                      interviewTime: "",
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleInterviewSubmit}
                  disabled={
                    !interviewForm.interviewDate || !interviewForm.interviewTime
                  }
                >
                  {selectedRegistration?.interviewDate ? (
                    <>
                      <i className="ti ti-edit-circle me-2" />
                      Update
                    </>
                  ) : (
                    <>
                      <i className="ti ti-calendar-plus me-2" />
                      Schedule
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewStatus;
